import { FactionCharacter } from "../../../User/User";
import { APIResponse, APIUtility } from "../../ApiUtility";
import { Punishment } from "../Account/AccountInterface";
import {
  AccountSessionFactionDTO,
  Faction,
  FactionAlliance,
  FactionMember,
  FactionPost,
  FactionRank,
} from "./FactionInterface";
import {
  FactionWarParticipation,
  War,
  WarAction,
  WarParticipant,
  WarSummary,
} from "./WarInterface";

export class SunsetAPIFaction {
  private apiUtility: APIUtility;
  private v1ApiUtility: APIUtility;
  public baseURL: string;

  constructor(baseURL: string = "https://ss-rp.com/api/v2/") {
    this.apiUtility = new APIUtility(baseURL);
    this.v1ApiUtility = new APIUtility("https://ss-rp.com/api/v1/");
    this.baseURL = baseURL;
  }

  async getFactions(): Promise<APIResponse<Faction[]>> {
    return await this.apiUtility.get("api/factions");
  }
  async getFactionByTag(tag: string): Promise<APIResponse<Faction>> {
    return await this.apiUtility.get(`api/factions/by-tag/${tag}`);
  }
  async getFactionRanksByTag(tag: string): Promise<APIResponse<FactionRank[]>> {
    return await this.apiUtility.get(`api/factions/ranks-by-tag/${tag}`);
  }
  async getFactionsByPartialName(
    name: string,
  ): Promise<APIResponse<Faction[]>> {
    return await this.apiUtility.get(`api/factions/by-partial-name/${name}`);
  }
  async getFactionMembersByTag(
    tag: string,
  ): Promise<APIResponse<FactionMember[]>> {
    return await this.apiUtility.get(`api/factions/members-by-tag/${tag}`);
  }
  async getFactionAllianceByTag(
    tag: string,
  ): Promise<APIResponse<FactionAlliance>> {
    return await this.apiUtility.get(`api/factions/alliance-by-tag/${tag}`);
  }
  async createFactionPost(
    request: FormData,
  ): Promise<APIResponse<FactionPost>> {
    return await this.apiUtility.post(
      "api/factions/create-faction-post",
      request,
    );
  }
  async getFactionActivity(
    tag: string,
  ): Promise<APIResponse<AccountSessionFactionDTO[]>> {
    return await this.apiUtility.get(`api/factions/activity-sessions/${tag}`);
  }
  async getRecentFactionPosts(
    page: number,
  ): Promise<APIResponse<FactionPost[]>> {
    return await this.apiUtility.get(
      `api/factions/recent-faction-posts/${page}`,
    );
  }

  async getFactionPostsByTag(
    tag: string,
    page: number,
  ): Promise<APIResponse<FactionPost[]>> {
    return await this.apiUtility.get(
      `api/factions/faction-posts-by-tag/${tag}/${page}`,
    );
  }
  async getFactionPunishmentsByTag(
    tag: string,
  ): Promise<APIResponse<Punishment[]>> {
    return await this.apiUtility.get(`api/factions/faction-punishments/${tag}`);
  }
  async deleteFactionPost(id: number): Promise<APIResponse<string>> {
    return await this.apiUtility.delete(
      `api/factions/delete-faction-post/${id}`,
    );
  }
  async toggleFactionPostPin(postID: number): Promise<APIResponse<boolean>> {
    return await this.apiUtility.put(
      `api/factions/faction-punishments/${postID}`,
    );
  }

  // ============================================================================
  // Faction Wars (V1 API)
  // ============================================================================

  /** GET /wars – All wars, newest first */
  async getWars(): Promise<APIResponse<War[]>> {
    return await this.v1ApiUtility.get("faction/wars");
  }

  /** GET /wars/active – Wars with no winner / not yet ended */
  async getActiveWars(): Promise<APIResponse<War[]>> {
    return await this.v1ApiUtility.get("faction/wars/active");
  }

  /** GET /wars/:warId – Single war + its participants */
  async getWarById(warId: number): Promise<APIResponse<War>> {
    return await this.v1ApiUtility.get(`faction/wars/${warId}`);
  }

  /** GET /wars/:warId/actions – All actions for a war */
  async getWarActions(warId: number): Promise<APIResponse<WarAction[]>> {
    return await this.v1ApiUtility.get(`faction/wars/${warId}/actions`);
  }

  /** GET /wars/:warId/participants – Participants for a war */
  async getWarParticipants(warId: number): Promise<APIResponse<WarParticipant[]>> {
    return await this.v1ApiUtility.get(`faction/wars/${warId}/participants`);
  }

  /** GET /wars/:warId/summary – Aggregated summary (action counts, money totals) */
  async getWarSummary(warId: number): Promise<APIResponse<WarSummary>> {
    return await this.v1ApiUtility.get(`faction/wars/${warId}/summary`);
  }

  /** GET /wars/participant/:factionId – All wars a faction participated in */
  async getWarsByFaction(factionId: number): Promise<APIResponse<FactionWarParticipation[]>> {
    return await this.v1ApiUtility.get(`faction/wars/participant/${factionId}`);
  }

  /** GET /wars/actions/user/:userId – All war actions involving a user */
  async getWarActionsByUser(userId: number): Promise<APIResponse<WarAction[]>> {
    return await this.v1ApiUtility.get(`faction/wars/actions/user/${userId}`);
  }

  /** GET /wars/actions/faction/:factionId – All war actions involving a faction */
  async getWarActionsByFaction(factionId: number): Promise<APIResponse<WarAction[]>> {
    return await this.v1ApiUtility.get(`faction/wars/actions/faction/${factionId}`);
  }
}
